/**
 * useShareJournal - Hook for sharing journals to community
 * Handles metadata generation and sharing state updates
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { config } from "@/lib/config";

const API_BASE_URL = config.BACKEND_API_URL;

interface ShareMetadata {
    tags: string[];
    description: string;
    cover_image_url: string;
}

interface ShareJournalParams {
    journalId: string;
    journalName: string;
    journalSummary?: string;
}

/**
 * Generate AI metadata for sharing (tags, description, cover image)
 * Calls backend endpoint for AI-generated tags, description, and cover image
 */
async function generateShareMetadata(params: ShareJournalParams): Promise<ShareMetadata> {
    // Get auth token
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const response = await fetch(`${API_BASE_URL}/api/journals/generate-share-metadata`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
            journal_id: params.journalId,
            journal_name: params.journalName,
            journal_summary: params.journalSummary || '',
        }),
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to generate share metadata: ${response.status} - ${errorText}`);
    }

    return response.json();
}

/**
 * Hook for sharing/unsharing a journal
 */
export const useShareJournal = () => {
    const queryClient = useQueryClient();
    const { user } = useAuth();

    return useMutation({
        mutationFn: async ({
            journalId,
            sharingMode,
            journalName,
            journalSummary,
        }: {
            journalId: string;
            sharingMode: 'community' | 'private' | null;
            journalName: string;
            journalSummary?: string;
        }) => {
            if (!user) throw new Error("Must be logged in to share");

            if (sharingMode === null) {
                // Un-share: Clear sharing fields
                const { error } = await supabase
                    .from('journals')
                    .update({
                        sharing_mode: null,
                        shared_at: null,
                        tags: null,
                        community_description: null,
                        community_cover_url: null,
                    })
                    .eq('id', journalId)
                    .eq('user_id', user.id);

                if (error) throw error;
                return { shared: false };
            }

            // Share: Generate metadata and update journal
            const metadata = await generateShareMetadata({
                journalId,
                journalName,
                journalSummary,
            });

            const { error } = await supabase
                .from('journals')
                .update({
                    sharing_mode: sharingMode,
                    shared_at: new Date().toISOString(),
                    shared_by_user_id: user.id,
                    tags: metadata.tags,
                    community_description: metadata.description,
                    community_cover_url: metadata.cover_image_url || null,
                })
                .eq('id', journalId)
                .eq('user_id', user.id);

            if (error) throw error;
            return { shared: true, metadata };
        },
        onSuccess: (result, { journalId }) => {
            // Invalidate journal query to refresh UI
            queryClient.invalidateQueries({ queryKey: ['journal', journalId] });
            queryClient.invalidateQueries({ queryKey: ['community-journals'] });

            if (result.shared) {
                toast.success("Journal shared to community! 🎉");
            } else {
                toast.success("Journal removed from community");
            }
        },
        onError: (error) => {
            console.error("Share error:", error);
            toast.error("Failed to update sharing settings");
        },
    });
};

/**
 * Hook for updating share metadata (tags, description)
 */
export const useUpdateShareMetadata = () => {
    const queryClient = useQueryClient();
    const { user } = useAuth();

    return useMutation({
        mutationFn: async ({
            journalId,
            tags,
            description,
        }: {
            journalId: string;
            tags?: string[];
            description?: string;
        }) => {
            if (!user) throw new Error("Must be logged in");

            const updates: Record<string, unknown> = {};
            if (tags !== undefined) updates.tags = tags;
            if (description !== undefined) updates.community_description = description;

            const { error } = await supabase
                .from('journals')
                .update(updates)
                .eq('id', journalId)
                .eq('user_id', user.id);

            if (error) throw error;
        },
        onSuccess: (_, { journalId }) => {
            queryClient.invalidateQueries({ queryKey: ['journal', journalId] });
            toast.success("Share settings updated");
        },
        onError: () => {
            toast.error("Failed to update settings");
        },
    });
};
